import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const DatePickerWrapper = styled('div')(({ theme }) => ({
  '& .react-datepicker-popper': {
    zIndex: 99999,
    position: 'fixed !important',
  },
  '& .react-datepicker': {
    fontFamily: theme.typography.fontFamily,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '8px',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
    fontSize: '14px',
    zIndex: 99999,
  },
  '& .react-datepicker-wrapper': {
    width: '100%',
    display: 'block'
  },
  '& .react-datepicker__input-container input': {
    width: '100%',
    padding: '12px',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.9)',
    '&::placeholder': {
      color: 'rgba(255, 255, 255, 0.5)'
    },
    '&:focus': {
      outline: 'none',
      borderColor: 'rgba(255, 255, 255, 0.5)',
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.1)'
    }
  }
}));

interface Counsellor {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  applicationToken?: string;
  applicationExpiryDate?: Date | string;
  applicationSubmitted: boolean;
  applicationSubmittedDate?: Date | string;
  interviewScheduled?: boolean;
  applicationStatus: string;
  status?: 'active' | 'inactive' | 'offboarded' | 'archived';
  isArchived?: boolean;
  feedbackRequestSent?: boolean;
  feedbackRequestSentDate?: Date | string;
  feedbackSubmitted?: boolean;
  feedbackSubmittedDate?: Date | string;
}

interface ApplicationManagementProps {
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const ApplicationManagement: React.FC<ApplicationManagementProps> = ({ onSuccess, onError }) => {
  const navigate = useNavigate();
  const [counsellors, setCounsellors] = useState<Counsellor[]>([]);
  const [filteredCounsellors, setFilteredCounsellors] = useState<Counsellor[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedCounsellor, setSelectedCounsellor] = useState<Counsellor | null>(null);
  const [newExpiryDate, setNewExpiryDate] = useState<Date | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchCounsellors = useCallback(async () => {
    try {
      const response = await api.get('/api/counsellors');
      
      console.log('Fetched counsellors:', response.data.length);
      console.log('Sample counsellor feedback fields:', response.data[0] ? {
        name: `${response.data[0].firstName} ${response.data[0].lastName}`,
        feedbackRequestSent: response.data[0].feedbackRequestSent,
        feedbackSubmitted: response.data[0].feedbackSubmitted,
        applicationExpiryDate: response.data[0].applicationExpiryDate,
        applicationSubmitted: response.data[0].applicationSubmitted
      } : 'No counsellors');
      
      if (response.data) {
        // Filter counsellors who:
        // 1. Have been issued an application form (has applicationToken)
        // 2. Haven't had an interview scheduled
        // 3. Haven't been declined/archived
        // 4. Application hasn't expired (or has expired but they submitted before expiry)
        const applicationsInProgress = response.data
          .filter((counsellor: Counsellor) => {
            // Exclude active counsellors (completed onboarding)
            if (counsellor.status === 'active') return false;
            
            // Exclude offboarded or archived counsellors
            if (counsellor.status === 'offboarded' || counsellor.status === 'archived') return false;
            
            // Must have an application token (form was issued) OR have an expiry date set
            if (!counsellor.applicationToken && !counsellor.applicationExpiryDate) return false;
            
            // Exclude counsellors past the application stage or in
            // terminal/exception states
            const excludedStatuses = new Set([
              'Accepted', 'Declined', 'Archived', 'Rejected', 'Offboarded',
              'Suspended', 'Pending Agreement', 'Active',
              'Interview Scheduled', 'Interview Completed',
              'Legal Information Requested', 'Legal Information Submitted',
              'Legal Info Requested', 'Legal Info Update Requested',
              'Legal Info Submitted', 'Legal Info Approved',
              'Legal Info Rejected', 'Onboarding Review',
              'Pending Self-Completion', 'Ready For Onboarding'
            ]);
            if (excludedStatuses.has(counsellor.applicationStatus)) return false;
            if (counsellor.interviewScheduled) return false;
            if (counsellor.isArchived) return false;
            
            // If they've submitted, keep them in the list until interview is scheduled
            if (counsellor.applicationSubmitted) return true;
            
            // Keep expired applications in the list so we can request feedback
            // (They will show "Request Feedback" button instead of being hidden)
            if (counsellor.applicationExpiryDate) {
              // Keep both active and expired applications
              return true;
            }
            
            return true;
          })
          .sort((a: Counsellor, b: Counsellor) => {
            // Sort by expiry date (soonest first)
            const dateA = a.applicationExpiryDate ? new Date(a.applicationExpiryDate).getTime() : 0;
            const dateB = b.applicationExpiryDate ? new Date(b.applicationExpiryDate).getTime() : 0;
            return dateA - dateB;
          });
        
        setCounsellors(applicationsInProgress);
        setFilteredCounsellors(applicationsInProgress);
      }
    } catch (error: any) {
      console.error('Error fetching counsellors:', error);
      onError('Failed to fetch applications');
      setCounsellors([]);
      onError('Failed to load counsellors');
    }
  }, [onError]);

  // Initial load with loading state
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await fetchCounsellors();
      setLoading(false);
    };
    loadInitialData();
  }, [fetchCounsellors]);

  // Auto-refresh every 30 seconds (silent, no loading state)
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      fetchCounsellors();
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, [fetchCounsellors]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = counsellors.filter(
      (counsellor) =>
        `${counsellor.firstName} ${counsellor.lastName}`.toLowerCase().includes(query) ||
        counsellor.email.toLowerCase().includes(query) ||
        counsellor.phone.toLowerCase().includes(query)
    );
    setFilteredCounsellors(filtered);
    setCurrentPage(1);
  };

  const formatDate = (dateString: string | Date | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const isExpiringSoon = (expiryDate: string | Date | undefined) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
  };

  const handleExtendExpiry = (counsellor: Counsellor) => {
    setSelectedCounsellor(counsellor);
    setNewExpiryDate(counsellor.applicationExpiryDate ? new Date(counsellor.applicationExpiryDate) : null);
    setShowExtendModal(true);
  };

  const handleResendApplication = (counsellor: Counsellor) => {
    setSelectedCounsellor(counsellor);
    setShowResendModal(true);
  };

  const handleRequestFeedback = (counsellor: Counsellor) => {
    setSelectedCounsellor(counsellor);
    setShowFeedbackModal(true);
  };

  const processExtendExpiry = async () => {
    if (!selectedCounsellor || !newExpiryDate) {
      onError('Please select a new expiry date');
      return;
    }

    try {
      setIsProcessing(true);
      await api.put(`/api/counsellors/${selectedCounsellor._id}/extend-token`, {
        newExpiry: newExpiryDate,
      });

      setShowExtendModal(false);
      setSelectedCounsellor(null);
      setNewExpiryDate(null);
      fetchCounsellors();
      onSuccess('Application expiry date extended successfully');
    } catch (error) {
      console.error('Error extending expiry date:', error);
      onError('Failed to extend expiry date');
    } finally {
      setIsProcessing(false);
    }
  };

  const processResendApplication = async () => {
    if (!selectedCounsellor) return;

    try {
      setIsProcessing(true);
      await api.post(`/api/counsellors/${selectedCounsellor._id}/resend-application`);

      setShowResendModal(false);
      setSelectedCounsellor(null);
      onSuccess('Application form resent successfully');
    } catch (error) {
      console.error('Error resending application:', error);
      onError('Failed to resend application form');
    } finally {
      setIsProcessing(false);
    }
  };

  const processFeedbackRequest = async () => {
    if (!selectedCounsellor) return;

    try {
      setIsProcessing(true);
      await api.post('/api/application-feedback/create', {
        counsellorId: selectedCounsellor._id
      });

      setShowFeedbackModal(false);
      setSelectedCounsellor(null);
      fetchCounsellors();
      onSuccess('Feedback request sent successfully');
    } catch (error: any) {
      console.error('Error sending feedback request:', error);
      onError(error.response?.data?.error || 'Failed to send feedback request');
    } finally {
      setIsProcessing(false);
    }
  };

  // Pagination
  const totalItems = filteredCounsellors.length;
  const totalPages = Math.max(Math.ceil(totalItems / itemsPerPage), 1);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCounsellors.slice(indexOfFirstItem, indexOfLastItem);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Typography variant="h6" className="theme-text-primary">
          Loading applications...
        </Typography>
      </div>
    );
  }

  return (
    <>
      {/* Info Banner */}
      <div className="mb-6 p-4 glass-card-light border-l-4 border-blue-400">
        <Typography variant="body1" className="theme-text-primary mb-2 font-semibold">
          Application Management
        </Typography>
        <Typography variant="body2" className="theme-text-secondary">
          Track counsellors who have been issued application forms. Applications will automatically disappear from this list once an interview is scheduled or they are declined.
        </Typography>
      </div>

      {/* Search and Refresh */}
      <div className="mb-6 flex gap-4">
        <input
          type="text"
          placeholder="Search by name, email, or phone..."
          value={searchQuery}
          onChange={handleSearch}
          className="theme-input flex-1"
        />
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchCounsellors}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            backgroundColor: 'rgba(100, 181, 246, 0.2)',
            color: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid rgba(100, 181, 246, 0.3)',
            '&:hover': {
              backgroundColor: 'rgba(100, 181, 246, 0.3)',
            }
          }}
        >
          Refresh
        </Button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto glass-card">
        <table className="min-w-full">
          <thead className="glass-card-light">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                Phone
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                Expiry Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                Submitted Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {currentItems.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center">
                  <Typography variant="body1" className="theme-text-secondary">
                    No applications in progress
                  </Typography>
                </td>
              </tr>
            ) : (
              currentItems.map((counsellor) => (
                <tr 
                  key={counsellor._id} 
                  className={`hover:bg-white/5 transition-colors duration-200 ${counsellor.applicationSubmitted ? 'cursor-pointer' : ''}`}
                  onClick={() => {
                    if (counsellor.applicationSubmitted) {
                      navigate(`/counsellors/${counsellor._id}`);
                    }
                  }}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium theme-text-primary">
                      {counsellor.firstName} {counsellor.lastName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm theme-text-muted">
                      {counsellor.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm theme-text-muted">
                      {counsellor.phone}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm theme-text-muted">
                      {formatDate(counsellor.applicationExpiryDate)}
                      {counsellor.applicationExpiryDate && new Date(counsellor.applicationExpiryDate) < new Date() && !counsellor.applicationSubmitted ? (
                        <Chip
                          label="Expired"
                          size="small"
                          sx={{
                            ml: 1,
                            backgroundColor: 'rgba(244, 67, 54, 0.2)',
                            color: 'rgba(244, 67, 54, 0.9)',
                            fontSize: '0.7rem',
                          }}
                        />
                      ) : isExpiringSoon(counsellor.applicationExpiryDate) && (
                        <Chip
                          label="Expiring Soon"
                          size="small"
                          sx={{
                            ml: 1,
                            backgroundColor: 'rgba(255, 152, 0, 0.2)',
                            color: 'rgba(255, 152, 0, 0.9)',
                            fontSize: '0.7rem',
                          }}
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {counsellor.applicationSubmitted ? (
                      <Chip
                        label="Submitted"
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(76, 175, 80, 0.2)',
                          color: 'rgba(76, 175, 80, 0.9)',
                        }}
                      />
                    ) : (
                      <Chip
                        label="Not Submitted"
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(158, 158, 158, 0.2)',
                          color: 'rgba(158, 158, 158, 0.9)',
                        }}
                      />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm theme-text-muted">
                      {counsellor.applicationSubmitted
                        ? formatDate(counsellor.applicationSubmittedDate)
                        : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {counsellor.applicationSubmitted ? (
                      <Chip
                        label={counsellor.applicationStatus || "Awaiting Interview"}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(255, 193, 7, 0.3)',
                          color: 'rgba(255, 235, 59, 1)',
                          fontWeight: 'bold',
                          border: '1px solid rgba(255, 193, 7, 0.5)',
                        }}
                      />
                    ) : (
                      <div className="flex flex-col space-y-2">
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExtendExpiry(counsellor);
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1 rounded transition-colors duration-200"
                          >
                            Extend Expiry
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResendApplication(counsellor);
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1 rounded transition-colors duration-200"
                          >
                            Resend Form
                          </button>
                        </div>
                        {/* Stage 1: Request Feedback - Application expired, no feedback request sent */}
                        {counsellor.applicationExpiryDate && 
                         new Date(counsellor.applicationExpiryDate) < new Date() && 
                         !counsellor.applicationSubmitted &&
                         !counsellor.feedbackRequestSent && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRequestFeedback(counsellor);
                            }}
                            className="bg-purple-600 hover:bg-purple-700 text-white text-xs px-3 py-1 rounded transition-colors duration-200 w-full mt-2"
                          >
                            Request Feedback
                          </button>
                        )}
                        
                        {/* Stage 2: Reissue Feedback Request - Feedback requested but not submitted */}
                        {counsellor.feedbackRequestSent && !counsellor.feedbackSubmitted && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRequestFeedback(counsellor);
                            }}
                            className="bg-orange-600 hover:bg-orange-700 text-white text-xs px-3 py-1 rounded transition-colors duration-200 w-full mt-2"
                          >
                            Reissue Feedback Request
                          </button>
                        )}
                        
                        {/* Stage 3: View Feedback - Feedback submitted */}
                        {counsellor.feedbackSubmitted && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/admin?tab=feedback');
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1 rounded transition-colors duration-200 w-full mt-2"
                          >
                            View Feedback
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalItems > 0 && (
        <div className="flex justify-between items-center mt-4 px-6 py-3 glass-card-light border-t border-white/10">
          <div>
            <p className="text-sm theme-text-secondary">
              Showing{' '}
              <span className="font-medium theme-text-primary">{indexOfFirstItem + 1}</span>
              {' - '}
              <span className="font-medium theme-text-primary">
                {Math.min(indexOfLastItem, totalItems)}
              </span>{' '}
              of <span className="font-medium theme-text-primary">{totalItems}</span> applications
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-2 rounded-md border text-sm font-medium transition-all duration-200 ${
                currentPage === 1
                  ? 'border-white/20 theme-text-muted cursor-not-allowed glass-card-light'
                  : 'theme-button-secondary'
              }`}
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-2 rounded-md border text-sm font-medium transition-all duration-200 ${
                currentPage === totalPages
                  ? 'border-white/20 theme-text-muted cursor-not-allowed glass-card-light'
                  : 'theme-button-secondary'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Extend Expiry Modal */}
      <Dialog
        open={showExtendModal}
        onClose={() => !isProcessing && setShowExtendModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)' }}>
          Extend Application Expiry
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.9)' }}>
            Extend the application expiry date for {selectedCounsellor?.firstName} {selectedCounsellor?.lastName}
          </Typography>
          <div className="mb-4">
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium', color: 'rgba(255, 255, 255, 0.9)' }}>
              New Expiry Date *
            </Typography>
            <DatePickerWrapper>
              <DatePicker
                selected={newExpiryDate}
                onChange={(date: Date | null) => {
                  if (date) {
                    const endOfDay = new Date(date);
                    endOfDay.setHours(23, 59, 59, 999);
                    setNewExpiryDate(endOfDay);
                  } else {
                    setNewExpiryDate(null);
                  }
                }}
                minDate={new Date()}
                dateFormat="dd/MM/yyyy"
                placeholderText="Select new expiry date"
                shouldCloseOnSelect
                showPopperArrow={false}
                popperPlacement="bottom-start"
              />
            </DatePickerWrapper>
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setShowExtendModal(false)}
            disabled={isProcessing}
            sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={processExtendExpiry}
            variant="contained"
            disabled={!newExpiryDate || isProcessing}
            sx={{
              backgroundColor: 'rgba(33, 150, 243, 0.2)',
              color: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid rgba(33, 150, 243, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(33, 150, 243, 0.3)',
              },
            }}
          >
            {isProcessing ? 'Processing...' : 'Extend Expiry'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resend Application Modal */}
      <Dialog
        open={showResendModal}
        onClose={() => !isProcessing && setShowResendModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)' }}>
          Resend Application Form
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.9)' }}>
            Are you sure you want to resend the application form to {selectedCounsellor?.firstName} {selectedCounsellor?.lastName}?
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            This will send a new email with the application link to {selectedCounsellor?.email}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setShowResendModal(false)}
            disabled={isProcessing}
            sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={processResendApplication}
            variant="contained"
            disabled={isProcessing}
            sx={{
              backgroundColor: 'rgba(76, 175, 80, 0.2)',
              color: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid rgba(76, 175, 80, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(76, 175, 80, 0.3)',
              },
            }}
          >
            {isProcessing ? 'Sending...' : 'Resend Application'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request Feedback Modal */}
      <Dialog
        open={showFeedbackModal}
        onClose={() => !isProcessing && setShowFeedbackModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)' }}>
          Request Application Feedback
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.9)' }}>
            Send a feedback request to {selectedCounsellor?.firstName} {selectedCounsellor?.lastName}?
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
            This will send an email asking why they didn't complete their application. The feedback will help improve the recruitment process.
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Email: {selectedCounsellor?.email}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setShowFeedbackModal(false)}
            disabled={isProcessing}
            sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={processFeedbackRequest}
            variant="contained"
            disabled={isProcessing}
            sx={{
              backgroundColor: 'rgba(156, 39, 176, 0.2)',
              color: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid rgba(156, 39, 176, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(156, 39, 176, 0.3)',
              },
            }}
          >
            {isProcessing ? 'Sending...' : 'Send Feedback Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApplicationManagement;
